import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { EditCreativeService, PublishService } from '@app/manage-view/services';
import { CampaignService, StudioCommentService } from '@app/shared/services';
import { EnvironmentService } from '@app/shared/services/environment.service';
import { SharedModule } from '@app/shared/shared.module';
import { CommentService, ThreadsDirective } from '@bannerflow/comments';
import { IPublishInfo } from '@studio/domain/campaign';
import { ICreative } from '@studio/domain/creativeset/creative';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'creative-list-item-meta',
    standalone: true,
    imports: [CommonModule, SharedModule],
    templateUrl: './creative-list-item-meta.component.html',
    styleUrls: ['./creative-list-item-meta.component.scss']
})
export class CreativeListItemMetaComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
    @Input() creative?: ICreative;
    @Input() inView: boolean;

    @Output() commentsToggled = new EventEmitter<boolean>();

    @ViewChild('commentsThread') commentsThread?: ThreadsDirective;

    isPublishable = false;
    publishInfo$: Observable<IPublishInfo | undefined>;
    commentsToken: string;
    commentsActive = false;
    inShowcase = false;
    inTP = false;

    private showCommentsTimeout?: number;
    private unsubscribe$ = new Subject<void>();

    constructor(
        private cdr: ChangeDetectorRef,
        private campaignService: CampaignService,
        private publishService: PublishService,
        private editCreativeService: EditCreativeService,
        private environmentService: EnvironmentService,
        public commentService: CommentService,
        private studioCommentService: StudioCommentService
    ) {
        this.commentsToken = this.studioCommentService.creativesToken;
        this.inShowcase = this.environmentService.inShowcaseMode;
    }

    ngOnInit(): void {
        if (!this.creative) {
            return;
        }

        this.inTP = this.environmentService.isPage('TP');

        this.publishInfo$ = this.campaignService.creativesPublished$.pipe(
            map(publishedCreatives =>
                publishedCreatives.find(
                    publishedCreative => publishedCreative.creative.id === this.creative?.id
                )
            ),
            filter(publishedCreatives => !!publishedCreatives),
            tap(publishInfo => {
                this.isPublishable =
                    this.publishService.isPublishable(this.creative!) && !publishInfo?.loading;
            }),
            takeUntil(this.unsubscribe$)
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.inView) {
            if (this.inView) {
                if (this.commentsActive) {
                    clearTimeout(this.showCommentsTimeout);
                    this.showCommentsTimeout = window.setTimeout(() => {
                        this.commentsThread!.openThreadOverlay();
                        this.detectChanges();
                    }, 750); // Comp must rendered before open the overlay
                }
            }
        }
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.commentsThread?.threadOpenToggle
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((b: boolean) => {
                    if (b !== this.commentsActive) {
                        this.commentsActive = b;
                        this.commentsToggled.emit(this.commentsActive);
                        this.detectChanges();
                    }
                });
        }, 2000);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();

        clearTimeout(this.showCommentsTimeout);
    }

    async publishChanges($event?: Event): Promise<void> {
        $event && $event.stopPropagation();

        if (this.inTP) {
            return;
        }
        this.publishService.publishCreatives(this.creative);
    }

    overrideTargetUrl(): void {
        if (!this.creative) {
            return;
        }
        this.editCreativeService.overrideTargetUrl([this.creative]);
    }

    openCampaign(id: string): void {
        this.campaignService.openCampaign(id);
    }

    private detectChanges(): void {
        this.cdr.detectChanges();
    }
}
